import moment from "moment";

/**
 * Mengambil waktu saat ini dalam UTC (Date object).
 * Contoh: 2025-10-17T04:00:00Z
 */
export const nowUTC = (): Date => {
  return new Date(new Date().toISOString());
};

/**
 * Mengambil waktu saat ini dalam format ISO string UTC.
 * Contoh: "2025-10-17T04:00:00Z"
 */
export const nowUTCString = (): string => {
  return new Date().toISOString();
};

/**
 * Mengubah tanggal lokal (dari input user atau DatePicker) ke string UTC (ISO 8601).
 * Contoh: "2025-10-17T10:00:00" (local user) → "2025-10-17T03:00:00Z"
 */
export const toUTCString = (date: string | Date): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  if (!d || Number.isNaN(d.getTime())) return "";
  // `Date` already represents an absolute instant; `toISOString()` returns the UTC form of that instant.
  // Do NOT re-apply timezone offsets, otherwise local selections will be shifted.
  return d.toISOString();
};

/**
 * Konversi tanggal-only (tanpa jam) ke UTC ISO string
 * tanpa pergeseran tanggal karena zona waktu.
 *
 * Misal: user pilih "2025-10-23" di Asia/Jakarta → tetap "2025-10-23T00:00:00Z"
 */
export const toUTCStringOnlyDate = (
  date: string | Date | null | undefined
): string => {
  if (!date) return "";
  const localDate = moment(date).startOf("day"); // 00:00 di waktu lokal
  const utcDate = moment.utc({
    year: localDate.year(),
    month: localDate.month(),
    date: localDate.date(),
  });
  return utcDate.toISOString();
};

/**
 * Parse string UTC dari backend (Go) ke Date lokal browser.
 * Misal: "2025-10-17T04:00:00Z" → Date lokal (WIB jadi 11:00:00)
 */
export const parseUTCToLocal = (utcString: string): Date => {
  return new Date(utcString);
};

/**
 * Format Date lokal ke string readable (untuk UI).
 * Contoh: 17 Oktober 2025, 11:00
 */
export const formatLocalReadable = (date: Date): string => {
  return date.toLocaleString("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

/**
 * Ambil timezone user dinamis (IANA format)
 * Contoh: "Asia/Jakarta", "America/New_York"
 */
export const getUserTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Konversi string UTC (backend) → tanggal lokal user TANPA bug “mundur 1 hari”.
 * Misal: "2025-10-17T00:00:00Z" → tetap 17 Oktober 2025 di zona user.
 */
export const utcToUserLocal = (utcString: string): moment.Moment => {
  return moment.utc(utcString).local();
};

/**
 * Format langsung dari UTC string (backend) → string readable sesuai zona user.
 * Misal: "2025-10-17T04:00:00Z" → "17 Okt 2025, 11:00" (Jakarta)
 */
export const formatUTCToUserReadable = (utcString: string): string => {
  return moment.utc(utcString).local().format("DD MMM YYYY, HH:mm");
};

/**
 * Konversi UTC string → YYYY-MM-DD lokal (untuk <input type="date">)
 * Aman, tidak akan mundur 1 hari.
 */
export const utcToLocalDateOnly = (utcString: string): string => {
  return moment.utc(utcString).local().format("YYYY-MM-DD");
};
